"use client";

import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
// import Heading from "@tiptap/extension-heading"; // Removed - using inline text sizing instead
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Extension } from "@tiptap/core";
import { DOMParser as ProseMirrorDOMParser, DOMSerializer } from "@tiptap/pm/model";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
  focus: () => void;
}

// Custom FontSize extension that extends TextStyle
const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
            parseHTML: element => element.style.fontSize || null,
          },
        },
      },
    ];
  },
});

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(({
  value,
  onChange,
  placeholder,
}, ref) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Disabled - using inline text sizing instead
        hardBreak: {
          keepMarks: true,
        },
        paragraph: {
          HTMLAttributes: {},
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'tiptap-bullet-list',
          },
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'tiptap-ordered-list',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'tiptap-list-item',
          },
        },
      }),
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Image.configure({
        inline: true,
        allowBase64: true, // Important for pasting images from Google Docs
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https', 'mailto', 'tel'],
        validate: href => /^https?:\/\//.test(href) || /^mailto:/.test(href) || /^tel:/.test(href),
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      Underline,
      TextStyle, // Must come before FontSize
      FontSize, // Extends TextStyle with fontSize attribute
    ],
    content: value,
    parseOptions: {
      preserveWhitespace: 'full', // Helps preserve formatting from Google Docs
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      console.log("Editor content updated:", html);
      onChange(html);
    },
    editorProps: {
      attributes: {
        class:
          "focus:outline-none min-h-[100px] p-3 prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-blue-800",
      },
      handleKeyDown(view, event) {
        // Better behavior for lists:
        // Enter = new paragraph (separate <p> tags)
        // Shift+Enter = line break within same paragraph
        if (event.key === 'Enter' && !event.shiftKey) {
          // Regular Enter - let default behavior create new paragraph
          const { state } = view;
          const { $from } = state.selection;

          // Check if we're in a list
          if ($from.parent.type.name === 'listItem') {
            // Let default list behavior handle it
            return false;
          }

          // For regular paragraphs, let default split behavior work
          return false;
        } else if (event.key === 'Enter' && event.shiftKey) {
          // Shift+Enter for hard break within same paragraph
          const { state, dispatch } = view;
          const hardBreak = state.schema.nodes.hardBreak;
          if (hardBreak) {
            const transaction = state.tr.replaceSelectionWith(hardBreak.create());
            dispatch(transaction);
            return true;
          }
        }
        return false;
      },
      handlePaste(view, event) {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Get HTML content
        const html = clipboardData.getData('text/html');

        if (html) {
          event.preventDefault();

          // Clean up the HTML before pasting
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          let cleanedHtml = '';

          // Process all elements
          doc.body.childNodes.forEach((node) => {
            if (node.nodeName === 'P' && node instanceof HTMLElement) {
              const innerHTML = node.innerHTML;

              // Check if paragraph contains <br> tags
              if (innerHTML.includes('<br')) {
                // Split by <br> tags into separate paragraphs
                const parts = innerHTML.split(/<br\s*\/?>/);

                parts.forEach(part => {
                  part = part.trim();
                  // Skip empty parts
                  if (part && part !== '&nbsp;' && part !== '') {
                    cleanedHtml += `<p>${part}</p>`;
                  }
                });
              } else {
                // No <br> tags, keep as is
                cleanedHtml += node.outerHTML;
              }
            } else if (node.nodeName === '#text') {
              // Text node - wrap in paragraph
              const text = node.textContent ? node.textContent.trim() : '';
              if (text) {
                cleanedHtml += `<p>${text}</p>`;
              }
            } else if (node.nodeType === 1 && node instanceof HTMLElement) {
              // Other element nodes (ul, ol, etc.)
              cleanedHtml += node.outerHTML;
            }
          });

          // Insert the cleaned HTML
          if (cleanedHtml && editor) {
            editor.chain().focus().insertContent(cleanedHtml).run();
          }

          return true;
        }

        // If no HTML, handle plain text
        const text = clipboardData.getData('text/plain');
        if (!text) return false;

        event.preventDefault();

        // Process plain text - each line becomes a paragraph
        const lines = text.split('\n');
        const paragraphs = lines
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => `<p>${line}</p>`)
          .join('');

        if (paragraphs && editor) {
          editor.chain().focus().insertContent(paragraphs).run();
        }

        return true;
      },
      handleDOMEvents: {
        keydown: (view, event) => {
          // Stop Enter key from propagating to form
          if (event.key === 'Enter') {
            event.stopPropagation();
            // Let Tiptap handle Enter normally to create paragraphs
            // Only use Shift+Enter for hard breaks
            return false;
          }
          // Return false to let TipTap continue processing
          return false;
        },
      },
    },
    immediatelyRender: false,
  });

  // Expose editor methods via ref
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (editor) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Get current selection position
          const { from } = editor.state.selection;

          // Insert content at the current position
          editor
            .chain()
            .focus()
            .insertContentAt(from, text)
            .run();
        });
      }
    },
    focus: () => {
      if (editor) {
        editor.chain().focus().run();
      }
    },
  }), [editor]);

  // Update editor content when value prop changes
  // But only if the editor is not currently focused (to avoid cursor jump)
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !editor.isFocused) {
      // Convert any <br> tags to paragraphs before setting content
      const cleanedValue = convertBreaksToParagraphs(value);
      editor.commands.setContent(cleanedValue);
    }
  }, [value, editor]);

  // Helper function to convert <br> tags to paragraphs
  const convertBreaksToParagraphs = (html: string) => {
    // Don't convert - keep the HTML as is to preserve line breaks
    // This was causing issues with intentional line breaks
    return html;
  };

  // Don't render editor until client is hydrated
  if (!isClient) {
    return (
      <div className="border rounded-md overflow-hidden bg-muted">
        <div className="border-b p-2 flex gap-1 bg-background">
          <div className="h-8 w-8 p-0 bg-muted rounded animate-pulse" />
          <div className="h-8 w-8 p-0 bg-muted rounded animate-pulse" />
          <div className="h-8 w-8 p-0 bg-muted rounded animate-pulse" />
          <div className="w-px bg-border mx-1" />
          <div className="h-8 w-8 p-0 bg-muted rounded animate-pulse" />
          <div className="h-8 w-8 p-0 bg-muted rounded animate-pulse" />
        </div>
        <div className="p-3 min-h-[100px] text-muted-foreground">
          Loading editor...
        </div>
      </div>
    );
  }

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <div className="border-b p-2 flex gap-1 bg-background items-center flex-wrap">

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={false}
          className={`h-8 w-8 p-0 ${
            editor.isActive("bold") ? "bg-accent" : ""
          }`}
        >
          <Bold className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={false}
          className={`h-8 w-8 p-0 ${
            editor.isActive("italic") ? "bg-accent" : ""
          }`}
        >
          <Italic className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={false}
          className={`h-8 w-8 p-0 ${
            editor.isActive("underline") ? "bg-accent" : ""
          }`}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const url = window.prompt('Enter URL:', 'https://');
            if (url) {
              if (editor.state.selection.empty) {
                // No text selected, insert link with text
                const text = window.prompt('Link text:', 'Link');
                if (text) {
                  editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
                }
              } else {
                // Text is selected, make it a link
                editor.chain().focus().setLink({ href: url }).run();
              }
            }
          }}
          className={`h-8 w-8 p-0 ${
            editor.isActive("link") ? "bg-accent" : ""
          }`}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>

        <div className="w-px bg-border mx-1" />

        {/* Text Size Selector - Using native select for better compatibility */}
        <select
          className="h-8 px-2 text-sm border rounded bg-background cursor-pointer"
          value={(() => {
            // Check current text size
            const marks = editor.state.selection.$from.marks();
            const textStyleMark = marks.find(mark => mark.type.name === 'textStyle');
            if (textStyleMark?.attrs?.fontSize) {
              return textStyleMark.attrs.fontSize;
            }
            return "normal";
          })()}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "normal") {
              editor.chain().focus().unsetMark('textStyle').run();
            } else {
              editor.chain().focus().setMark('textStyle', { fontSize: value }).run();
            }
          }}
        >
          <option value="normal">Normal</option>
          <option value="0.875em">Small</option>
          <option value="1.25em">Large</option>
          <option value="1.5em">X-Large</option>
          <option value="1.75em">Huge</option>
        </select>

        <div className="w-px bg-border mx-1" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            // Simple toggle - let Tiptap handle the conversion
            editor.chain().focus().toggleBulletList().run();
          }}
          disabled={false}
          className={`h-8 w-8 p-0 ${
            editor.isActive("bulletList") ? "bg-accent" : ""
          }`}
        >
          <List className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            // Simple toggle - let Tiptap handle the conversion
            editor.chain().focus().toggleOrderedList().run();
          }}
          disabled={false}
          className={`h-8 w-8 p-0 ${
            editor.isActive("orderedList") ? "bg-accent" : ""
          }`}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px bg-border mx-1" />

        {/* Alignment buttons */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`h-8 w-8 p-0 ${
            editor.isActive({ textAlign: 'left' }) ? "bg-accent" : ""
          }`}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`h-8 w-8 p-0 ${
            editor.isActive({ textAlign: 'center' }) ? "bg-accent" : ""
          }`}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`h-8 w-8 p-0 ${
            editor.isActive({ textAlign: 'right' }) ? "bg-accent" : ""
          }`}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </Button>



      </div>

      <EditorContent editor={editor} className="rich-text-editor" />

      <style jsx>{`
        :global(.rich-text-editor .ProseMirror) {
          direction: ltr;
          text-align: left;
          outline: none;
          color: inherit;
          word-wrap: break-word;
          word-break: break-word;
        }

        :global(.rich-text-editor .ProseMirror > * + *) {
          margin-top: 0.25em;
        }

        :global(.rich-text-editor .ProseMirror p) {
          margin: 0.25rem 0;
          line-height: 1.4;
          min-height: 1.4em;
        }

        :global(.rich-text-editor .ProseMirror p:empty) {
          min-height: 0.5rem;
          margin: 0.25rem 0;
        }

        :global(.rich-text-editor .ProseMirror p:empty::before) {
          content: '\u00A0';
          display: inline-block;
          width: 0;
        }

        /* Heading styles */
        :global(.rich-text-editor .ProseMirror h1) {
          font-size: 1.875rem;
          font-weight: 700;
          margin: 0.5rem 0 0.25rem 0;
          line-height: 1.3;
        }

        :global(.rich-text-editor .ProseMirror h2) {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0.4rem 0 0.25rem 0;
          line-height: 1.3;
        }

        :global(.rich-text-editor .ProseMirror h3) {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.35rem 0 0.25rem 0;
          line-height: 1.3;
        }

        :global(.rich-text-editor .ProseMirror h4) {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0.3rem 0 0.25rem 0;
          line-height: 1.3;
        }

        :global(.rich-text-editor .ProseMirror ul) {
          list-style: none !important;
          padding-left: 0 !important;
          margin: 0.5rem 0 !important;
          display: block !important;
        }

        :global(.rich-text-editor .ProseMirror ol) {
          list-style: none !important;
          padding-left: 0 !important;
          margin: 0.5rem 0 !important;
          display: block !important;
          counter-reset: item;
        }

        :global(.rich-text-editor .ProseMirror ul ul) {
          margin: 0 !important;
          padding-left: 1.5em !important;
        }

        :global(.rich-text-editor .ProseMirror ol ol) {
          margin: 0 !important;
          padding-left: 1.5em !important;
          counter-reset: item;
        }

        /* Bullet points using ::before */
        :global(.rich-text-editor .ProseMirror ul > li) {
          display: block !important;
          margin-bottom: 0.125rem !important;
          padding-left: 1.5em !important;
          position: relative !important;
        }

        :global(.rich-text-editor .ProseMirror ul > li::before) {
          content: "•" !important;
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          display: block !important;
          width: 1em !important;
          text-align: center !important;
        }

        /* Nested bullets */
        :global(.rich-text-editor .ProseMirror ul ul > li) {
          padding-left: 1.5em !important;
        }

        :global(.rich-text-editor .ProseMirror ul ul > li::before) {
          content: "◦" !important;
        }

        /* Ordered list numbers using ::before */
        :global(.rich-text-editor .ProseMirror ol > li) {
          display: block !important;
          margin-bottom: 0.125rem !important;
          padding-left: 1.5em !important;
          position: relative !important;
          counter-increment: item;
        }

        :global(.rich-text-editor .ProseMirror ol > li::before) {
          content: counter(item) "." !important;
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          display: block !important;
          width: 1em !important;
          text-align: right !important;
        }

        /* Nested ordered lists */
        :global(.rich-text-editor .ProseMirror ol ol) {
          counter-reset: item;
        }

        :global(.rich-text-editor .ProseMirror ol ol > li) {
          padding-left: 1.5em !important;
        }

        :global(.rich-text-editor .ProseMirror ol ol > li::before) {
          content: counter(item, lower-alpha) "." !important;
        }

        :global(.rich-text-editor .ProseMirror strong) {
          font-weight: 600;
        }

        :global(.rich-text-editor .ProseMirror em) {
          font-style: italic;
        }

        :global(.rich-text-editor .ProseMirror u) {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
});

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
